/**
 * Crockford Base32 — Family invite codes (CLAUDE.md §7, §10)
 *
 * Why Crockford? It excludes 0/O, 1/I/L, U from the alphabet specifically to
 * avoid confusion when a parent reads the code over the phone or types it
 * from a printed sheet. The 12-char "XXXX-XXXX-XXXX" format is also
 * tap-friendly on iPad: short, segmented, capital-letter-only.
 *
 * Alphabet (32 chars): 0123456789ABCDEFGHJKMNPQRSTVWXYZ
 * Excluded: I, L, O, U (per Douglas Crockford's spec — first three look like
 * digits, U excluded to avoid accidental obscenity).
 *
 * Validation accepts both:
 *   - "XXXX-XXXX-XXXX" (canonical, what we display + share)
 *   - "XXXXXXXXXXXX"   (12-char unhyphenated, what users sometimes type)
 * and folds Crockford's "lenient mode" (l→1, I→1, O→0, accept lowercase) on
 * input only — output always uses canonical alphabet + dashes.
 *
 * Entropy: 32^12 = 2^60 ≈ 1.15 × 10^18 codes. With 14-day expiry + 5/min/IP
 * rate limit (CLAUDE.md §10), brute force is infeasible (would need 10^11
 * years at 5/min).
 */

export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 12; // 4-4-4 grouping
export const CODE_CANONICAL_REGEX = /^[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;

/**
 * Generate a cryptographically random 12-char Crockford Base32 code,
 * formatted as "XXXX-XXXX-XXXX".
 *
 * Uses Web Crypto's getRandomValues with rejection sampling so the modulo
 * is unbiased. (256 % 32 = 0, but we still reject any byte ≥ 256 - (256 % 32)
 * = 0 in the abstract — the constants happen to align so any byte is fine.
 * We keep the rejection-sampling shape anyway for safety against future
 * alphabet length changes.)
 *
 * @throws if Web Crypto is unavailable (server contexts must use the Deno
 *   variant in supabase/functions/_shared/crockford.ts).
 */
export function generateFamilyCode(): string {
  if (typeof globalThis.crypto?.getRandomValues !== 'function') {
    throw new Error('Web Crypto API unavailable; cannot generate secure family code.');
  }

  const chars: string[] = [];
  // Sample more bytes than we need for rejection-sampling headroom.
  const bytes = new Uint8Array(CODE_LENGTH * 2);
  globalThis.crypto.getRandomValues(bytes);

  let byteIndex = 0;
  while (chars.length < CODE_LENGTH) {
    if (byteIndex >= bytes.length) {
      // Should not happen with 2× headroom, but defend anyway.
      const more = new Uint8Array(CODE_LENGTH);
      globalThis.crypto.getRandomValues(more);
      bytes.set(more, 0);
      byteIndex = 0;
    }
    const byte = bytes[byteIndex]!;
    byteIndex++;
    // Reject bytes that would skew the distribution. 256 = 8 × 32, so this
    // bound is exactly 256 (no bytes rejected) — kept defensive in case the
    // alphabet ever grows or shrinks.
    const max = Math.floor(256 / CROCKFORD_ALPHABET.length) * CROCKFORD_ALPHABET.length;
    if (byte >= max) continue;
    chars.push(CROCKFORD_ALPHABET[byte % CROCKFORD_ALPHABET.length]!);
  }

  return formatCode(chars.join(''));
}

/**
 * Insert dashes every 4 characters: "ABCDEFGHJKMN" → "ABCD-EFGH-JKMN".
 * Input must already be 12 raw alphabet characters; otherwise the output
 * is undefined.
 */
export function formatCode(raw: string): string {
  return `${raw.slice(0, 4)}-${raw.slice(4, 8)}-${raw.slice(8, 12)}`;
}

/**
 * Strip dashes and uppercase, then return the 12-char raw form. Returns
 * null if the input contains characters outside the Crockford alphabet
 * (after the lenient I/L→1, O→0 mapping).
 *
 * Use cases:
 *   - User typed "abcd-efgh-jkmn" lowercase → upcase
 *   - User typed "abcd efgh jkmn" with spaces → strip
 *   - User typed "AB-CD-EF-GH-JK-MN" → strip
 */
export function parseCode(input: string): string | null {
  // Strip whitespace, dashes, then upper-case.
  const stripped = input.replace(/[\s-]/g, '').toUpperCase();
  if (stripped.length !== CODE_LENGTH) return null;

  // Lenient lookalike folding (Crockford §3): I/L → 1, O → 0.
  const folded = stripped.replace(/[IL]/g, '1').replace(/O/g, '0');

  // Validate every char is in the Crockford alphabet (no U).
  for (const ch of folded) {
    if (!CROCKFORD_ALPHABET.includes(ch)) return null;
  }

  return folded;
}

/**
 * Validate that the given input parses to a canonical 12-char code.
 * Used at form-submit time and on the server before DB lookup.
 */
export function isValidCode(input: string): boolean {
  return parseCode(input) !== null;
}

/**
 * Auto-format a partial input as the user types: "abcdEFGH" → "ABCD-EFGH",
 * "ABCDEFGHJKMN" → "ABCD-EFGH-JKMN". Drops invalid characters silently
 * (Crockford forgiveness — better UX than yelling at the user).
 *
 * Truncates to CODE_LENGTH raw characters max.
 */
export function autoFormatCode(input: string): string {
  // 1. Strip everything except letters/digits + dashes.
  // 2. Upper-case.
  // 3. Drop invalid Crockford characters (I, L, O, U).
  // 4. Insert dashes at positions 4 and 8.
  const stripped = input.replace(/[\s-]/g, '').toUpperCase();
  // Apply lenient folding: I/L → 1, O → 0 (so the user can keep typing).
  const folded = stripped.replace(/[IL]/g, '1').replace(/O/g, '0');

  let cleaned = '';
  for (const ch of folded) {
    if (CROCKFORD_ALPHABET.includes(ch)) cleaned += ch;
    if (cleaned.length === CODE_LENGTH) break;
  }

  if (cleaned.length === 0) return '';
  if (cleaned.length <= 4) return cleaned;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
}
